import { createSlice } from '@reduxjs/toolkit';
import { defaultOffset, defaultPageSize } from 'appConfig';
import { AppDispatch } from 'redux/store';
import { ANNOUNCEMENTS, Announcements, AnnouncementsTabs } from 'types/announcements';
import { Baskets, BasketsTabs } from 'types/baskets';
import { TableState } from 'types/common';
import { GIVING_BASKETS } from 'utils/constants';

export type TabComponents = 'announcement' | 'baskets';
interface SetCurrentTabPayload {
    component: TabComponents;
    currentTab: AnnouncementsTabs | BasketsTabs;
}

interface AppState {
    announcement: Announcements;
    baskets: Baskets;
    table: TableState;
}

const tableInitialState = {
    pageNumber: 0,
    pageSize: defaultPageSize,
    offset: defaultOffset,
};

const initialState: AppState = {
    announcement: {
        currentTab: ANNOUNCEMENTS,
    },
    baskets: {
        currentTab: GIVING_BASKETS,
    },
    table: tableInitialState,
};

export const setAnnouncementsTab = (currentTab: AnnouncementsTabs) => (dispatch: AppDispatch) => {
    dispatch(
        setCurrentTab({
            component: 'announcement',
            currentTab,
        })
    );
};
export const setBasketsTab = (currentTab: BasketsTabs) => (dispatch: AppDispatch) => {
    dispatch(
        setCurrentTab({
            component: 'baskets',
            currentTab,
        })
    );
};

const appSlice = createSlice({
    name: 'appSlice',
    initialState,
    reducers: {
        setCurrentTab: (state, action: { payload: SetCurrentTabPayload }) => {
            const payload = action?.payload || {};
            if (!payload) return;
            const { component, currentTab } = payload;
            if (!component || !currentTab) return;
            if (component) state[component].currentTab = currentTab;
        },
        updateTableState: (state, action: { payload: TableState }) => {
            const { payload } = action;
            if (!payload) return;
            state.table = { ...state.table, ...payload };
        },
        /** Writes only `offset` — used by StyledTable's parentOffset sync (avoids spreading tableState). */
        setTableOffset: (state, action: { payload: number }) => {
            state.table.offset = action.payload;
        },
        /** Writes only `pageNumber`/`offset` back to 0, leaving `pageSize` untouched — used by
         *  StyledTable's unmount cleanup. `pageSize` is a user-chosen preference (rows-per-page),
         *  not per-screen transient state; resetting it on every unmount silently reverted a
         *  user's chosen rows-per-page whenever a tab switch unmounted/remounted the Table
         *  (e.g. Community.tsx / Announcements.tsx, which swap in a different Table-bearing
         *  component per tab) even when that switch's own handler had just deliberately
         *  preserved it. */
        resetTablePage: (state) => {
            state.table.pageNumber = tableInitialState.pageNumber;
            state.table.offset = tableInitialState.offset;
        },
    },
});

export const { setCurrentTab, updateTableState, setTableOffset, resetTablePage } = appSlice.actions;

export default appSlice.reducer;
